import { useEffect } from 'react';

import { extractClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { type Edge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/types';
import { reorderWithEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/util/reorder-with-edge';
import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';

import { type monitorRuleCardsParams } from './types';

export function useMonitorRuleCards<T extends { id: string }>({
	droppableId,
	data,
	onComplete,
}: monitorRuleCardsParams<T>) {
	useEffect(() => {
		return monitorForElements({
			canMonitor: ({ initial: { dropTargets } }) => {
				if (!data || !dropTargets.length) {
					return false;
				}
				const id = dropTargets[0].data.droppableId;
				return id === droppableId;
			},
			onDrop({ source, location }) {
				const [destination] = location.current.dropTargets;

				if (!destination) {
					return;
				}

				const destinationId = destination.data.value;
				const sourceId = source.data.value;

				if (typeof destinationId !== 'string' || typeof sourceId !== 'string') {
					return;
				}

				const startIndex = data.findIndex((item) => item.id === sourceId);
				const indexOfTarget = data.findIndex((item) => item.id === destinationId);

				const closestEdgeOfTarget: Edge | null = extractClosestEdge(destination.data);

				const updated = reorderWithEdge({
					list: data,
					startIndex,
					indexOfTarget,
					closestEdgeOfTarget,
					axis: 'vertical',
				});

				onComplete({
					updatedValue: updated,
					startIndex,
					targetIndex: indexOfTarget,
					sourceId,
					destinationId,
				});
			},
		});
	}, [data, droppableId, onComplete]);
}
